/**
 * Cloud state — the learner's record lives in the database, never on the device.
 *
 * Why this exists
 * ---------------
 * Every personal thing the app remembers (an assessment in progress, the
 * Career DNA™ snapshot, saved careers, roadmap progress, goals, dashboard
 * layout) used to sit in `localStorage`. That means a learner who signs in on
 * a phone after answering on a laptop sees an empty app, and a cleared browser
 * loses work. At onboarding scale that is not acceptable.
 *
 * So this module is the single store. Shape:
 *
 *   - One table, `public.user_state`, keyed `(user_id, key)`. One row per
 *     stored item per person: no table sprawl, one indexed read per sign-in,
 *     and it stays flat at hundreds of thousands of learners.
 *   - On sign-in we pull every row for the learner once into an in-memory
 *     cache. Reads are synchronous against that cache, so the existing
 *     screens keep their simple `read()` shape and never wait on the network.
 *   - Writes update the cache immediately and are batched to the database on a
 *     short debounce, plus on tab-hide and before unload. A failed write is
 *     retried; the cache is never rolled back, so nothing a learner did is
 *     discarded because the network blinked.
 *
 * The API is deliberately `localStorage`-shaped (`getItem`/`setItem`/
 * `removeItem`) so it is a drop-in for the surfaces that used to call it.
 * Device-only, non-personal flags (debug switches, OAuth handshake state,
 * presenter mode) intentionally do NOT belong here.
 */
import { supabase } from "@/integrations/supabase/client";
import { isRlsDenial, recordHydrationFallback, recordOps, setOpsUser } from "@/lib/observability";

/**
 * Keys written before this store existed, rescued once on first sign-in.
 *
 * Enumerated deliberately: `wekit_` also covers device-only things (debug
 * switches, the remembered payment email, the last-uid marker) that must NOT
 * follow a learner between devices.
 */
const LEGACY_PREFIXES = [
  "wcc360.",
  "wekit.dashboard.",
  "wekit:vb:",
  "wekit_trait_scores",
  "wekit_assessment_id",
  "wekit_interview_career",
  "wekit_saved_careers",
  "wekit_compare_careers",
  "wekit_saved_jobs",
  "wekit_favourites",
  "wekit_bookmarks",
  "wekit_student_quickstart_checklist",
  "wekit_passion_ids",
  "wekit_quiz_boost",
  "wekit_answers",
];


type Row = { key: string; value: { raw: string } | null };

const cache = new Map<string, string>();
const dirty = new Set<string>();
const removed = new Set<string>();

let userId: string | null = null;
let ready = false;
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let flushing: Promise<void> | null = null;

const listeners = new Set<() => void>();

function notify() {
  listeners.forEach((fn) => { try { fn(); } catch { /* a listener must not break a write */ } });
}

/** Subscribe to hydration and to every change, local or pulled. */
export function subscribeCloudState(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/** True once the learner's rows have been pulled from the database. */
export function isCloudStateReady(): boolean { return ready; }

/** The signed-in learner this cache belongs to, or null. */
export function cloudStateUser(): string | null { return userId; }

// ------------------------------------------------------------------ read/write

export function getItem(key: string): string | null {
  return cache.has(key) ? (cache.get(key) as string) : null;
}

export function setItem(key: string, value: string): void {
  cache.set(key, value);
  removed.delete(key);
  dirty.add(key);
  schedule();
  notify();
}

export function removeItem(key: string): void {
  cache.delete(key);
  dirty.delete(key);
  removed.add(key);
  schedule();
  notify();
}

/** Every key currently held, for callers that need to sweep a prefix. */
export function keys(): string[] { return [...cache.keys()]; }

/** JSON convenience wrappers — the shape most callers actually want. */
export function readJson<T>(key: string, fallback: T): T {
  const raw = getItem(key);
  if (raw === null) return fallback;
  try { return JSON.parse(raw) as T; } catch { return fallback; }
}

export function writeJson(key: string, value: unknown): void {
  try { setItem(key, JSON.stringify(value)); }
  catch { /* unserialisable value: nothing sensible to store */ }
}

/**
 * `localStorage`-shaped facade so a surface can swap one identifier and keep
 * its existing calls.
 */
export const cloudStorage = {
  getItem,
  setItem,
  removeItem,
  readJson,
  writeJson,
};

// ------------------------------------------------------------------- hydration

/**
 * Pull the learner's whole record. Called on sign-in and on every auth change.
 * Resolves even on failure — an empty cache is a degraded read, never a crash,
 * and the next write still queues.
 */
export async function hydrateCloudState(uid: string): Promise<void> {
  if (userId && userId !== uid) resetCloudState();
  userId = uid;
  setOpsUser(uid);

  const legacy = harvestLegacy();
  const startedAt = typeof performance !== "undefined" ? performance.now() : Date.now();
  let rows = 0;

  try {
    const { data, error } = await supabase
      .from("user_state")
      .select("key, value")
      .eq("user_id", uid);
    if (error) throw error;
    for (const row of (data ?? []) as Row[]) {
      const raw = row.value && typeof row.value.raw === "string" ? row.value.raw : null;
      if (raw !== null) cache.set(row.key, raw);
    }
    rows = (data ?? []).length;
    recordOps({
      area: "hydration",
      name: "user_state.pull",
      ok: true,
      duration_ms: Math.round((typeof performance !== "undefined" ? performance.now() : Date.now()) - startedAt),
      props: { rows },
    });
  } catch (e) {
    // A degraded pull means this learner is about to be shown state that did
    // not come from their record. That is the fallback we most want to see.
    recordOps({
      area: "hydration",
      name: "user_state.pull",
      ok: false,
      duration_ms: Math.round((typeof performance !== "undefined" ? performance.now() : Date.now()) - startedAt),
      error_code: (e as { code?: string })?.code ?? "unknown",
      error_message: String((e as { message?: string })?.message ?? e).slice(0, 300),
      denied: isRlsDenial(e),
      hydration_fallback: true,
    });
    // eslint-disable-next-line no-console
    console.error("[cloudStore] hydrate failed", e);
  }

  // Anything this device held that the cloud doesn't know about is a run that
  // predates this store. Adopt it once, then take it off the device.
  let adopted = false;
  for (const [key, value] of legacy) {
    if (!cache.has(key)) { cache.set(key, value); dirty.add(key); adopted = true; }
    try { localStorage.removeItem(key); } catch { /* ignore */ }
  }
  if (adopted) recordHydrationFallback("legacy_device_adopted", { keys: legacy.size });

  ready = true;
  notify();
  if (adopted) schedule(0);
}

/** Everything under a managed prefix that is still sitting on this device. */
function harvestLegacy(): Map<string, string> {
  const found = new Map<string, string>();
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !LEGACY_PREFIXES.some((p) => key.startsWith(p))) continue;
      const value = localStorage.getItem(key);
      if (value !== null) found.set(key, value);
    }
  } catch { /* private mode: nothing to rescue */ }
  return found;
}

/**
 * Sign-out: the next learner on this device must start clean.
 *
 * `ready` stays true because a signed-out visitor is a *known* state, not a
 * pending one — the discovery flow must still run for them, in memory, and
 * anything they answered before signing in is pushed the moment they do.
 */
export function resetCloudState(): void {
  cache.clear();
  dirty.clear();
  removed.clear();
  userId = null;
  ready = true;
  setOpsUser(null);
  if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
  notify();
}


// --------------------------------------------------------------------- flushing

function schedule(delay = 400) {
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = setTimeout(() => { flushTimer = null; void flushCloudState(); }, delay);
}

/** Push every pending change. Safe to call at any time; never throws. */
export async function flushCloudState(): Promise<void> {
  if (flushing) return flushing;
  const uid = userId;
  if (!uid) return;
  if (dirty.size === 0 && removed.size === 0) return;

  const upserts = [...dirty].map((key) => ({
    user_id: uid,
    key,
    value: { raw: cache.get(key) ?? "" },
  }));
  const deletes = [...removed];
  dirty.clear();
  removed.clear();

  flushing = (async () => {
    const startedAt = typeof performance !== "undefined" ? performance.now() : Date.now();
    const elapsed = () =>
      Math.round((typeof performance !== "undefined" ? performance.now() : Date.now()) - startedAt);
    try {
      if (upserts.length) {
        const { error } = await supabase
          .from("user_state")
          .upsert(upserts as never, { onConflict: "user_id,key" });
        if (error) throw error;
      }
      if (deletes.length) {
        const { error } = await supabase
          .from("user_state")
          .delete()
          .eq("user_id", uid)
          .in("key", deletes);
        if (error) throw error;
      }
      recordOps({
        area: "state_write",
        name: "user_state.flush",
        ok: true,
        duration_ms: elapsed(),
        props: { upserts: upserts.length, deletes: deletes.length },
      });
    } catch (e) {
      // Put the work back so nothing is lost, and try again shortly.
      upserts.forEach((r) => dirty.add(r.key));
      deletes.forEach((k) => removed.add(k));
      recordOps({
        area: "state_write",
        name: "user_state.flush",
        ok: false,
        duration_ms: elapsed(),
        error_code: (e as { code?: string })?.code ?? "unknown",
        error_message: String((e as { message?: string })?.message ?? e).slice(0, 300),
        denied: isRlsDenial(e),
        props: { upserts: upserts.length, deletes: deletes.length },
      });
      // eslint-disable-next-line no-console
      console.error("[cloudStore] flush failed, will retry", e);
      schedule(4000);
    } finally {
      flushing = null;
    }
  })();

  return flushing;
}

if (typeof window !== "undefined") {
  // A closed tab must not cost a learner their last answer.
  window.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") void flushCloudState();
  });
  window.addEventListener("pagehide", () => { void flushCloudState(); });
  window.addEventListener("online", () => { void flushCloudState(); });
}
